import React from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import { RESPONSIVE_SPACING, RESPONSIVE_TYPOGRAPHY } from '@/design-tokens/responsive';
import { motion } from 'framer-motion';

interface ParentLoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  variant?: 'spinner' | 'skeleton' | 'pulse';
  showMessage?: boolean;
}

export const ParentLoadingState: React.FC<ParentLoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  className,
  variant = 'spinner',
  showMessage = true
}) => {
  const { isMobile } = useResponsive();
  
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: isMobile ? 'h-6 w-6' : 'h-8 w-8',
    lg: isMobile ? 'h-8 w-8' : 'h-12 w-12'
  };

  const textSizeClasses = {
    sm: RESPONSIVE_TYPOGRAPHY.caption,
    md: RESPONSIVE_TYPOGRAPHY.body,
    lg: RESPONSIVE_TYPOGRAPHY.bodyLarge
  };

  // Skeleton loading variant
  if (variant === 'skeleton') {
    return (
      <div className={cn(
        'space-y-4 animate-pulse',
        RESPONSIVE_SPACING.section.mobile,
        className
      )}>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  // Pulse loading variant  
  if (variant === 'pulse') {
    return (
      <motion.div 
        className={cn(
          'flex items-center justify-center',
          RESPONSIVE_SPACING.section.mobile,
          className
        )}
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className={cn(
          'rounded-full bg-primary/20',
          sizeClasses[size]
        )} />
      </motion.div>
    );
  }

  // Default spinner variant
  return (
    <motion.div 
      className={cn(
        'flex flex-col items-center justify-center space-y-3',
        RESPONSIVE_SPACING.section.mobile,
        className
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Loader2 className={cn('animate-spin text-primary', sizeClasses[size])} />
      {showMessage && (
        <p className={cn(
          'text-gray-600 font-medium text-center',
          textSizeClasses[size]
        )}>
          {message}
        </p>
      )}
    </motion.div>
  );
};

// Enhanced loading components for different use cases
export const ParentSkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn(
    'bg-white rounded-2xl border shadow-sm animate-pulse',
    RESPONSIVE_SPACING.card.padding,
    className
  )}>
    <div className="space-y-3">
      <div className="h-6 bg-gray-200 rounded w-1/2"></div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-4/5"></div>
      </div>
    </div>
  </div>
);

export const ParentSkeletonList: React.FC<{ items?: number; className?: string }> = ({ 
  items = 3, 
  className 
}) => (
  <div className={cn('space-y-4', className)}>
    {Array.from({ length: items }).map((_, i) => (
      <ParentSkeletonCard key={i} />
    ))}
  </div>
);

export default ParentLoadingState;